import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { isNative, isIOS } from '../../services/native';

const APPLE_CLIENT_ID = 'com.hisabjourney.web';
const APPLE_REDIRECT_URI = 'https://app.hisabjourney.com/apple-callback';

// Detect if current device/browser is Apple (iOS, macOS, Safari)
const isApplePlatform = (() => {
  if (isNative) return isIOS; // Native app: only iOS
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  // iOS devices (iPhone, iPad, iPod)
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPad with desktop Safari (reports as MacIntel)
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  // macOS Safari/Chrome
  if (/Macintosh|Mac OS X/.test(ua)) return true;
  return false;
})();

export default function AppleLoginButton({ onSuccess, onError, text = 'sign_in' }) {
  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(isNative); // Native doesn't need SDK
  const { appleLogin } = useAuth();

  // Load Apple JS SDK for web (only on Apple platforms)
  useEffect(() => {
    if (isNative || !isApplePlatform) return;

    if (window.AppleID) {
      setSdkReady(true);
      return;
    }

    if (document.querySelector('script[src*="appleid.auth"]')) {
      // Script exists but not loaded yet, wait for it
      const check = setInterval(() => {
        if (window.AppleID) { setSdkReady(true); clearInterval(check); }
      }, 100);
      return () => clearInterval(check);
    }

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => setSdkReady(true);
    script.onerror = () => console.error('Failed to load Apple Sign-In SDK');
    document.head.appendChild(script);
  }, []);

  // Don't render on non-Apple platforms
  if (!isApplePlatform) return null;

  // Wait for SDK (web only) — render placeholder to prevent layout jump
  if (!sdkReady) {
    return (
      <div className="w-full h-[48px] rounded-xl bg-black/5 animate-pulse" />
    );
  }

  const handleAppleSignIn = async () => {
    if (loading) return; // Prevent double-click
    setLoading(true);
    try {
      let identityToken, givenName, familyName;

      if (isIOS) {
        // Native iOS — use Capacitor plugin
        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
        const result = await SignInWithApple.authorize({
          clientId: 'com.hisabjourney.app',
          redirectURI: APPLE_REDIRECT_URI,
          scopes: 'email name'
        });
        identityToken = result.response.identityToken;
        givenName = result.response.givenName;
        familyName = result.response.familyName;
      } else {
        // Web — use Apple JS SDK
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true
        });
        const result = await window.AppleID.auth.signIn();
        identityToken = result.authorization.id_token;
        // Apple only sends name on first authorization
        givenName = result.user?.name?.firstName;
        familyName = result.user?.name?.lastName;
      }

      if (!identityToken) {
        throw new Error('No identity token received from Apple');
      }

      const loginResult = await appleLogin({
        identityToken,
        fullName: (givenName || familyName) ? { givenName, familyName } : null
      });
      onSuccess?.(loginResult);
    } catch (err) {
      console.error('Apple Sign-In error:', err);
      // User cancelled
      if (err?.code === '1001' || err?.message?.includes('cancel') || err?.error === 'popup_closed_by_user') {
        return;
      }
      onError?.(err.error || err.message || 'Apple Sign-In failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleAppleSignIn}
      disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-surface-300 rounded-xl bg-black hover:bg-gray-900 transition-colors disabled:cursor-not-allowed relative"
    >
      <svg className={`w-5 h-5 text-white ${loading ? 'opacity-30' : ''}`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
      <span className={`text-sm font-medium text-white ${loading ? 'opacity-30' : ''}`}>
        {text === 'sign_up' ? 'Sign up with Apple' : 'Sign in with Apple'}
      </span>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </button>
  );
}
